import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../../helpers/axiosInstance";
import useAuth from "../../../../hooks/useAuth";
import { Async, Banner, Button, Card, Confirm, Dialog, Empty, Field, Page, Pill, SectionTitle, useAction, useFetch } from "../../../../shared/ui/ui";
import "./roles.css";

// Roles and permissions, as the app has them: what each role in the fund may
// do, section by section. The console had no page for this at all, so roles
// could only be changed from a phone.
//
// Each section folds out to a switch per action -- "View savings", "View all
// members' savings", "Add savings" -- with one on its heading that turns them
// all on or off. View is the section and your own records; View all is
// everybody's. Without View nothing else means anything, so turning it off
// takes the rest with it and turning any other on turns it on. The server
// keeps the same rule, and decides who may read, change, add and delete roles
// from the Roles and permissions section itself.

const useRoles = () => {
  const { organisation } = useAuth();
  return useFetch(`/api/security/${organisation._id}/roles`);
};
const useFeatures = () => {
  const { organisation } = useAuth();
  return useFetch(`/api/security/${organisation._id}/features`);
};

const sectionsHeld = (role) => Object.values(role.permissions || {}).filter((permission) => permission?.view).length;

export const Roles = () => {
  const navigate = useNavigate();
  const { organisation, can } = useAuth();
  const roles = useRoles();
  const features = useFeatures();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const { busy, run } = useAction();

  const add = async () => {
    if (!name.trim()) return;
    const body = await run(() => axiosInstance.post(`/api/security/${organisation._id}/roles`, { name: name.trim() }), {
      done: (data) => data.message || "Role added.",
    });
    if (body) {
      setAdding(false);
      setName("");
      roles.reload();
      if (body.role?.key) navigate(`/admin/roles/${body.role.key}`);
    }
  };

  return (
    <Page
      title="Roles and permissions"
      subtitle="What each role may do in this fund. A member can hold one role here and a different one in another fund."
      actions={
        can("security", "add") ? (
          <Button variant="primary" onClick={() => setAdding(true)}>
            + Add role
          </Button>
        ) : null
      }
    >
      <Async state={roles}>
        {(list) =>
          !list.length ? (
            <Empty title="No roles">This fund has no roles yet.</Empty>
          ) : (
            <Card flush>
              {list.map((role) => (
                <button key={role.key} className="sf-row sf-row-button" onClick={() => navigate(`/admin/roles/${role.key}`)}>
                  <div className="sf-row-main">
                    <p className="sf-row-title">{role.name}</p>
                    <p className="sf-row-sub">
                      {role.members} {role.members === 1 ? "member" : "members"} · {sectionsHeld(role)} of {features.data?.features?.length || "…"} sections
                    </p>
                  </div>
                  <div className="sf-row-end sf-chips">
                    {role.loan_approver ? <Pill>Approver</Pill> : null}
                    {role.loan_sanctioner ? <Pill>Sanctioner</Pill> : null}
                  </div>
                </button>
              ))}
            </Card>
          )
        }
      </Async>
      {adding ? (
        <Dialog
          title="Add a role"
          onClose={busy ? undefined : () => setAdding(false)}
          actions={
            <>
              <Button variant="text" onClick={() => setAdding(false)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="primary" busy={busy} disabled={!name.trim()} onClick={add}>
                Add
              </Button>
            </>
          }
        >
          <Field label="Name" help="It starts with nothing allowed. Choose what it may do next.">
            <input value={name} autoFocus onChange={(event) => setName(event.target.value)} onKeyDown={(event) => event.key === "Enter" && add()} />
          </Field>
        </Dialog>
      ) : null}
    </Page>
  );
};

/**
 * What the switch for one action says: "View savings", "View all members'
 * savings" -- or the catalogue's own words where those would not fit, as on
 * the dashboard ("View fund details").
 */
const actionLabel = (feature, action) => {
  if (feature.labels?.[action]) return feature.labels[action];
  const what = feature.name.toLowerCase();
  const verbs = { view: "View", view_all: "View all members'", add: "Add", edit: "Edit", delete: "Delete", approve: "Approve" };
  return `${verbs[action] || action} ${what}`;
};

const actionHelp = (feature, action) =>
  feature.help?.[action] ||
  (action === "view" && (feature.actions || []).includes("view_all") ? "Only their own" : action === "view_all" ? "Every member of the fund" : null);

export const RoleDetail = () => {
  const { roleKey } = useParams();
  const navigate = useNavigate();
  const { organisation, can, refreshAccess } = useAuth();
  const roles = useRoles();
  const features = useFeatures();
  const [draft, setDraft] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { busy, run } = useAction();
  const mayEdit = can("security", "edit");

  const role = roles.data?.find((candidate) => candidate.key === roleKey);
  // Start again from the server's copy whenever it arrives — on opening, and
  // after a save once the reloaded role is in. Resetting on save itself would
  // copy the role as it was before the save and show the change as unsaved.
  useEffect(() => {
    if (role) setDraft(JSON.parse(JSON.stringify(role)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles.data, roleKey]);

  const groups = useMemo(() => {
    const grouped = new Map();
    for (const feature of features.data?.features || []) {
      if (!grouped.has(feature.group)) grouped.set(feature.group, []);
      grouped.get(feature.group).push(feature);
    }
    return [...grouped.entries()];
  }, [features.data]);

  const [open, setOpen] = useState({});
  const permission = (key) => draft?.permissions?.[key] || {};

  // What the role's loan part keeps on: a mandatory approver or a sanctioner
  // decides other members' applications, so sees every one; an approver has
  // to be able to approve. The server enforces the same on save.
  const lockedFor = (key) => {
    if (key !== "loan_applications" || !draft) return [];
    const deciding = draft.loan_approver || draft.loan_sanctioner;
    return [...(deciding ? ["view", "view_all"] : []), ...(draft.loan_approver ? ["approve"] : [])];
  };

  const setPermission = (key, action, value) =>
    setDraft((current) => {
      const next = { ...current, permissions: { ...current.permissions } };
      const section = { ...(next.permissions[key] || {}) };
      section[action] = value;
      // Without view nothing else means anything, so it all goes; anything
      // else needs view.
      if (action === "view" && !value) {
        for (const other of Object.keys(section)) section[other] = false;
      }
      if (action !== "view" && value) section.view = true;
      next.permissions[key] = section;
      return next;
    });

  /** The heading's switch: every action in the section on, or all off. */
  const setAll = (feature, value) =>
    setDraft((current) => {
      const section = {};
      for (const action of feature.actions || ["view"]) section[action] = value;
      for (const action of lockedFor(feature.key)) section[action] = true;
      return { ...current, permissions: { ...current.permissions, [feature.key]: section } };
    });

  /** A loan role, with what it implies -- as the app and the server do. */
  const setLoanRole = (key, value) =>
    setDraft((current) => {
      const next = { ...current, [key]: value };
      const section = { ...(next.permissions?.loan_applications || {}) };
      if (next.loan_approver || next.loan_sanctioner) Object.assign(section, { view: true, view_all: true });
      if (next.loan_approver) section.approve = true;
      return { ...next, permissions: { ...next.permissions, loan_applications: section } };
    });

  const dirty = draft && role && JSON.stringify(draft) !== JSON.stringify(role);

  const save = async () => {
    const ok = await run(
      () =>
        axiosInstance.put(`/api/security/${organisation._id}/roles/${role.key}`, {
          name: draft.name,
          permissions: draft.permissions,
          loan_approver: draft.loan_approver,
          loan_sanctioner: draft.loan_sanctioner,
        }),
      { done: (data) => data.message || "Saved." }
    );
    if (ok) {
      roles.reload();
      // Changing a role can change what its holders may do — this person too.
      refreshAccess();
    }
  };

  const remove = async () => {
    const ok = await run(() => axiosInstance.delete(`/api/security/${organisation._id}/roles/${role.key}`), {
      done: (data) => data.message || "Role deleted.",
    });
    if (ok) navigate("/admin/roles");
  };

  return (
    <Async state={roles}>
      {() =>
        !role ? (
          <Page title="Role" back>
            <Empty title="Role not found">It may have been deleted.</Empty>
          </Page>
        ) : (
          <Page
            title={role.name}
            subtitle={`${role.members} ${role.members === 1 ? "member holds" : "members hold"} this role.`}
            back
            actions={
              mayEdit ? (
                <Button variant="primary" busy={busy} disabled={!dirty} onClick={save}>
                  Save
                </Button>
              ) : null
            }
          >
            {!draft || !features.data ? null : (
              <>
                {mayEdit ? (
                  <Field label="Name">
                    <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
                  </Field>
                ) : null}
                {groups.map(([group, list]) => (
                  <section key={group}>
                    <SectionTitle title={group} />
                    <Card flush>
                      {list.map((feature) => {
                        const held = permission(feature.key);
                        const actions = feature.actions || ["view"];
                        const on = actions.filter((action) => held[action]).length;
                        const locked = lockedFor(feature.key);
                        const expanded = Boolean(open[feature.key]);
                        return (
                          <div key={feature.key} className="roles-section">
                            <div className="sf-row roles-heading">
                              <button
                                type="button"
                                className="roles-toggle"
                                aria-expanded={expanded}
                                onClick={() => setOpen({ ...open, [feature.key]: !expanded })}
                              >
                                <span className={`roles-chevron ${expanded ? "roles-chevron-open" : ""}`} aria-hidden="true">
                                  ›
                                </span>
                                <span className="sf-row-main">
                                  <span className="sf-row-title" style={{ opacity: held.view ? 1 : 0.55 }}>
                                    {feature.name}
                                  </span>
                                  <span className="sf-row-sub">{on === 0 ? "Off" : on === actions.length ? "All on" : `${on} of ${actions.length} on`}</span>
                                </span>
                              </button>
                              <label className="roles-switch" title="Select all">
                                <input
                                  type="checkbox"
                                  checked={on === actions.length}
                                  disabled={!mayEdit}
                                  onChange={(event) => setAll(feature, event.target.checked)}
                                  aria-label={`${feature.name}: select all`}
                                />
                                <span />
                              </label>
                            </div>
                            {expanded ? (
                              <div className="roles-actions">
                                {actions.map((action) => {
                                  const help = actionHelp(feature, action);
                                  const isLocked = locked.includes(action);
                                  return (
                                    <div key={action} className="sf-row roles-action">
                                      <div className="sf-row-main">
                                        <p className="sf-row-title">{actionLabel(feature, action)}</p>
                                        {help || isLocked ? <p className="sf-row-sub">{isLocked ? "Always on: this role has a part in approving loans." : help}</p> : null}
                                      </div>
                                      <label className="roles-switch">
                                        <input
                                          type="checkbox"
                                          checked={Boolean(held[action])}
                                          disabled={!mayEdit || isLocked}
                                          onChange={(event) => setPermission(feature.key, action, event.target.checked)}
                                          aria-label={actionLabel(feature, action)}
                                        />
                                        <span />
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </Card>
                  </section>
                ))}

                <SectionTitle title="Loan approvals" />
                <p className="sf-muted">Who has to approve a loan application, and who may sanction it.</p>
                <Card flush>
                  {[
                    ["loan_approver", "Loan approver", "Everyone in this role is a mandatory approver: an application cannot be sanctioned until all of them have approved it."],
                    ["loan_sanctioner", "Loan sanctioner", "Everyone in this role may sanction a loan, once every mandatory approver has approved it."],
                  ].map(([key, title, text]) => (
                    <div key={key} className="sf-row">
                      <div className="sf-row-main">
                        <p className="sf-row-title">{title}</p>
                        <p className="sf-row-sub">{text}</p>
                      </div>
                      <label className="roles-switch">
                        <input type="checkbox" checked={Boolean(draft[key])} disabled={!mayEdit} onChange={(event) => setLoanRole(key, event.target.checked)} aria-label={title} />
                        <span />
                      </label>
                    </div>
                  ))}
                </Card>

                {can("security", "delete") ? (
                  <div style={{ marginTop: "2rem" }}>
                    <Button variant="danger" block disabled={role.members > 0} onClick={() => setConfirmDelete(true)}>
                      Delete this role
                    </Button>
                    {role.members > 0 ? (
                      <Banner>
                        {`${role.members} ${role.members === 1 ? "member holds" : "members hold"} this role. Move them to another one first — the fund will not let it be deleted while anybody has it.`}
                      </Banner>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
            {confirmDelete ? (
              <Confirm title={`Delete ${role.name}?`} confirmLabel="Delete" danger busy={busy} onCancel={() => setConfirmDelete(false)} onConfirm={remove}>
                Nobody holds it, so nothing else changes. This cannot be undone.
              </Confirm>
            ) : null}
          </Page>
        )
      }
    </Async>
  );
};
